import type {
  Action,
  ActionExample,
  HandlerCallback,
  IAgentRuntime,
  Memory,
  State,
} from '@elizaos/core';
import { ModelType, logger, parseKeyValueXml } from '@elizaos/core';
import type { ZapperConfig } from '../types';
import type {
  TransactionDetailsContent,
  TransactionDetailsData,
  TransactionEventV2,
} from '../types/transaction';
import { ZapperGraphQLClient, ZapperGraphQLUtils } from '../utils/zapperGraphQL';

// ✅ REPLACE complex hash extraction with simple function
function extractTransactionHash(text: string, state?: State): string | null {
  logger.debug('🔍 Extracting transaction hash', {
    textLength: text?.length || 0,
    hasState: !!state,
  });

  if (!text) return null;

  // Simple but reliable pattern for transaction hash extraction
  const hashPattern = /0x[a-fA-F0-9]{64}/;
  const match = text.match(hashPattern);

  if (match) {
    logger.info('✅ Transaction hash found', {
      hash: match[0],
    });
    return match[0];
  }

  logger.debug('❌ No transaction hash found in text');
  return null;
}

const getTransactionDetailsTemplate = `Respond with an XML block containing only the extracted values. Use key-value pairs.

Example response for transaction details request:
<response>
    <hash>0x1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef</hash>
    <chainId>1</chainId>
    <index>0</index>
    <subject>0x1234567890abcdef1234567890abcdef12345678</subject>
</response>

## Recent Messages

{{recentMessages}}

Given the recent messages, extract the following information about the transaction details request:
- Hash: The transaction hash to get details for (required)
- ChainId: The blockchain chain ID (default: 1 for Ethereum)
- Index: Optional transaction index for multi-transaction scenarios
- Subject: Optional wallet address for perspective-specific details

Respond with an XML block containing only the extracted values.`;

export const getTransactionDetailsAction: Action = {
  name: 'GET_TRANSACTION_DETAILS',
  similes: [
    'GET_TX_DETAILS',
    'TRANSACTION_DETAILS',
    'SHOW_TX_DETAILS',
    'VIEW_TRANSACTION_DETAILS',
    'ANALYZE_TRANSACTION',
    'TRANSACTION_ANALYSIS',
    'TX_BREAKDOWN',
    'TRANSACTION_INFO',
  ],
  description:
    'Get detailed information about a specific transaction including events, gas usage, and interpretations',
  validate: async (runtime: IAgentRuntime, message: Memory, state?: State): Promise<boolean> => {
    logger.info('🔍 Starting GET_TRANSACTION_DETAILS validation');

    // Check API key
    const apiKey = runtime.getSetting('ZAPPER_API_KEY');
    if (!apiKey) {
      logger.error('❌ ZAPPER_API_KEY not configured');
      return false;
    }

    // Check for required data (transaction hash)
    const messageText = message.content.text || '';
    const hash = extractTransactionHash(messageText, state);

    if (!hash) {
      logger.warn('⚠️ No transaction hash found for GET_TRANSACTION_DETAILS');
      return false;
    }

    logger.info('✅ Validation passed', {
      hash,
      dataType: 'transaction_hash',
    });

    return true;
  },
  handler: async (
    runtime: IAgentRuntime,
    message: Memory,
    state?: State,
    options?: { [key: string]: unknown },
    callback?: HandlerCallback
  ): Promise<boolean> => {
    logger.info('🚀 Starting GET_TRANSACTION_DETAILS handler');

    try {
      // 1. Get configuration
      const apiKey = runtime.getSetting('ZAPPER_API_KEY');
      const apiUrl = runtime.getSetting('ZAPPER_API_URL') || 'https://public.zapper.xyz/graphql';

      if (!apiKey) {
        throw new Error('ZAPPER_API_KEY not configured');
      }

      // 2. State composition
      logger.log('📝 Step 1: Composing state...');
      let currentState = state;
      if (!currentState) {
        logger.debug('Creating new state from message');
        currentState = await runtime.composeState(message);
      } else {
        logger.debug('Using existing state, composing with recent messages');
        currentState = await runtime.composeState(message, ['RECENT_MESSAGES']);
      }

      // 3. Prompt preparation
      logger.log('🎯 Step 2: Preparing prompt...');
      const prompt = getTransactionDetailsTemplate.replace(
        '{{recentMessages}}',
        currentState.recentMessagesData || ''
      );

      // 4. Model processing
      logger.log('🤖 Step 3: Processing with language model...');
      const result = await runtime.useModel(ModelType.TEXT_SMALL, {
        prompt,
        stopSequences: [],
      });

      // 5. XML parsing
      logger.log('🔍 Step 4: Parsing XML content...');
      const content = parseKeyValueXml(result) as Partial<TransactionDetailsContent>;

      if (!isTransactionDetailsContent(runtime, content)) {
        const helpMessage = `I need a transaction hash to get details. Please provide:

• Transaction hash (e.g., 0x1234...5678)
• Include chain ID if not Ethereum (e.g., "on polygon")

Try: "Show details for transaction 0x1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef"`;

        callback?.({
          text: helpMessage,
          content: { error: 'No transaction hash provided' },
        });
        return false;
      }

      logger.info('📍 Using hash for lookup', { hash: content.hash });

      // 3. Initialize GraphQL client
      const client = new ZapperGraphQLClient({
        apiKey,
        apiUrl,
        debug: true,
        timeout: 30000,
        retryAttempts: 3,
      });

      // 4. Parse additional parameters
      const chainId =
        typeof content.chainId === 'string'
          ? Number.parseInt(content.chainId, 10)
          : content.chainId || 1; // Default to Ethereum

      const index =
        typeof content.index === 'string' ? Number.parseInt(content.index, 10) : content.index;

      // 5. Prepare GraphQL query and variables
      const query = `
        query GetTransactionDetails(
          $hash: String!
          $chainId: Int!
          $index: Float
          $subject: Address
        ) {
          transactionDetailsV2(
            hash: $hash
            chainId: $chainId
            index: $index
            subject: $subject
          ) {
            id
            hash
            eventHash
            network
            index
            fromAddress {
              address
            }
            toAddress {
              address
            }
            methodSighash
            timestamp
            interpreterId
            value
            rawInput
            gas
            app {
              displayName
              websiteUrl
            }
            interpretation {
              processedDescription
              description
            }
            perspective
            methodSignature
            transaction {
              hash
              gasPrice
              blockNumber
              timestamp
            }
          }
        }
      `;

      const variables = {
        hash: content.hash,
        chainId,
        index,
        subject: content.subject,
      };

      logger.info('📡 Making GraphQL request to Zapper API');

      // 6. Execute query using GraphQL client
      const response = await client.query(query, variables);

      if (ZapperGraphQLUtils.hasErrors(response)) {
        const errorMessage = response.errors
          ? ZapperGraphQLUtils.formatGraphQLErrors(response.errors)
          : 'Unknown GraphQL error';
        logger.error('❌ GraphQL errors in response', { errors: response.errors });
        throw new Error(`GraphQL errors: ${errorMessage}`);
      }

      // 7. Process response data safely
      const responseData = response.data?.transactionDetailsV2;

      if (!responseData || !Array.isArray(responseData) || responseData.length === 0) {
        logger.warn('⚠️ No transaction details found', { hash: content.hash });

        callback?.({
          text: `No transaction details found for hash ${content.hash}.`,
          content: {
            hash: content.hash,
            chainId,
            message: 'No data found',
          },
        });
        return true;
      }

      logger.info('✅ Data found, processing results', {
        resultCount: responseData.length,
      });

      // 8. Format the response data
      const formattedResponse = processTransactionDetailsData(responseData, content.hash, chainId);

      logger.info('✅ Response formatted successfully', {
        hash: formattedResponse.hash,
        eventsCount: formattedResponse.eventsCount,
      });

      const responseText = generateTransactionDetailsResponseText(formattedResponse);

      callback?.({
        text: responseText,
        content: formattedResponse,
      });

      logger.info('✅ GET_TRANSACTION_DETAILS handler completed successfully');
      return true;
    } catch (error) {
      logger.error('❌ Error in GET_TRANSACTION_DETAILS handler', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });

      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

      callback?.({
        text: `Unable to get transaction details: ${errorMessage}`,
        content: { error: errorMessage },
      });

      return false;
    }
  },
  examples: [
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Show details for transaction 0x1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'Found transaction with 3 events and detailed breakdown',
          action: 'GET_TRANSACTION_DETAILS',
        },
      },
    ],
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Analyze this transaction: 0xabc123def456',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'Transaction analysis complete with gas usage and event details',
          action: 'GET_TRANSACTION_DETAILS',
        },
      },
    ],
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Show transaction details without hash',
        },
      },
      {
        name: '{{agent}}',
        content: {
          text: 'I need a transaction hash to get details. Please provide a transaction hash (0x...).',
          action: 'GET_TRANSACTION_DETAILS',
        },
      },
    ],
  ] as ActionExample[][],
};

function isTransactionDetailsContent(
  _runtime: IAgentRuntime,
  content: Partial<TransactionDetailsContent>
): content is TransactionDetailsContent {
  logger.debug('🔍 isTransactionDetailsContent: Starting validation');

  // Check if content exists
  if (!content) {
    logger.error('❌ Validation failed: content is null/undefined');
    return false;
  }

  // Check hash
  if (typeof content.hash !== 'string' || content.hash.length === 0) {
    logger.error('❌ Validation failed: hash is not a non-empty string');
    return false;
  }

  logger.log('✅ isTransactionDetailsContent: All validation checks passed');
  return true;
}

// ✅ Safe data processing
function processTransactionDetailsData(
  data: TransactionEventV2[],
  hash: string,
  chainId: number
): {
  hash: string;
  chainId: number;
  status: string;
  gasFee: string;
  gasUsed: string;
  value: string;
  network: string;
  timestamp: string;
  eventsCount: number;
  mainEvent: {
    description: string;
    app?: string;
  };
  additionalEvents: number;
} {
  const mainEvent = data[0];
  const additionalEvents = data.slice(1);

  // Calculate gas fee safely
  const transaction = mainEvent
    ? (
        mainEvent as TransactionEventV2 & {
          transaction?: { gasPrice?: string; timestamp?: string; blockNumber?: number };
        }
      ).transaction
    : null;
  const gasUsed = '21000'; // Standard ETH transfer gas (mock value)
  const gasPrice = transaction?.gasPrice || '0';
  const gasFee = (Number.parseFloat(gasUsed) * Number.parseFloat(gasPrice)).toString();

  return {
    hash,
    chainId,
    status: 'success', // Mock value as status not available
    gasFee,
    gasUsed,
    value: mainEvent?.value || '0',
    network: mainEvent?.network || 'unknown',
    timestamp: transaction?.timestamp || mainEvent?.timestamp || '',
    eventsCount: data.length,
    mainEvent: {
      description:
        mainEvent?.interpretation?.processedDescription ||
        mainEvent?.interpretation?.description ||
        'Transaction',
      app: mainEvent?.app?.displayName,
    },
    additionalEvents: additionalEvents.length,
  };
}

function generateTransactionDetailsResponseText(data: {
  hash: string;
  status: string;
  gasFee: string;
  value: string;
  network: string;
  timestamp: string;
  eventsCount: number;
  mainEvent: { description: string; app?: string };
  additionalEvents: number;
}): string {
  const gasFeeEth = (Number.parseFloat(data.gasFee) / 1e18).toFixed(6);
  const valueEth = (Number.parseFloat(data.value) / 1e18).toFixed(6);

  return `Transaction Details for ${data.hash}:

📋 Status: ${data.status}
⛽ Gas Fee: ${gasFeeEth} ETH
💰 Value: ${valueEth} ETH
🌐 Network: ${data.network}
📅 ${formatTimestamp(data.timestamp)}

🎯 Main Event:
${data.mainEvent.description}

${data.additionalEvents > 0 ? `\n📜 Additional Events: ${data.additionalEvents}` : ''}

${data.mainEvent.app ? `\n🔗 App: ${data.mainEvent.app}` : ''}`;
}

function formatTimestamp(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    return date.toLocaleString();
  } catch {
    return timestamp;
  }
}
